import { useMemo, useState } from "react";
import { ToggleButton } from "primereact/togglebutton";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { DeveloperProfileConstants } from "sj-ui-toolkit/src/constants/developerProfile";

export type AiStack = Record<string, string[]>;

interface AiOption {
  label: string;
  value: string;
}

interface AiStackCategoryProps {
  category: {
    key: string;
    label: string;
    options: AiOption[];
  };
  selected: string[];
  onSelectionChange: (v: string[]) => void;
}

const normalizeValue = (label: string) => label.toLowerCase().replace(/\s/g, "_");

function AiStackCategory({ category, selected, onSelectionChange }: AiStackCategoryProps) {
  const [customOptions, setCustomOptions] = useState<AiOption[]>([]);
  const [draft, setDraft] = useState("");

  const options = useMemo(() => {
    const known = [...category.options, ...customOptions];
    // selected values saved outside the known lists still need a pill to render
    const unlisted = selected
      .filter(v => !known.some(option => option.value === v))
      .map(v => ({ label: v, value: v }));

    return [...known, ...unlisted];
  }, [category.options, customOptions, selected]);

  const toggle = (optionValue: string) => {
    onSelectionChange(
      selected.includes(optionValue)
        ? selected.filter(v => v !== optionValue)
        : [...selected, optionValue]
    );
  };

  const addDraft = () => {
    const label = draft.trim();
    if (!label) return;

    const newValue = normalizeValue(label);
    if (!options.some(option => option.value === newValue)) {
      setCustomOptions(prev => [...prev, { label, value: newValue }]);
    }
    if (!selected.includes(newValue)) {
      onSelectionChange([...selected, newValue]);
    }
    setDraft("");
  };

  const onDraftKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Enter adds the entry instead of submitting the wizard form
    if (e.key === "Enter") {
      e.preventDefault();
      addDraft();
    }
  };

  return (
    <div
      role="group"
      aria-labelledby={`aiStack_${category.key}_label`}
      className="flex flex-col gap-2"
    >
      <span id={`aiStack_${category.key}_label`} className="body-md">
        {category.label}
      </span>
      <div className="flex flex-wrap items-center gap-2 pl-4">
        {options.map(option => (
          <ToggleButton
            key={option.value}
            className="p-button-sm"
            data-testid={`ai-stack-toggle-${category.key}-${option.value}`}
            checked={selected.includes(option.value)}
            onChange={() => toggle(option.value)}
            onLabel={option.label}
            offLabel={option.label}
            onIcon="pi pi-check"
          />
        ))}
        <div className="flex items-center gap-2">
          <InputText
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={onDraftKeyDown}
            placeholder="Add your own"
            data-testid={`ai-stack-draft-input-${category.key}`}
            aria-label={`Add a ${category.label} option`}
            className="p-inputtext-sm w-40"
          />
          <Button
            type="button"
            label="Add"
            data-testid={`ai-stack-add-button-${category.key}`}
            outlined
            size="small"
            onClick={addDraft}
            disabled={!draft.trim()}
          />
        </div>
      </div>
    </div>
  );
}

interface AiStackSelectorProps {
  value: AiStack;
  onChange: (v: AiStack) => void;
}

// Nested-group selector for the four AI stack categories: each group renders
// its options as toggle pills plus an inline input for values we don't offer.
export function AiStackSelector({ value, onChange }: AiStackSelectorProps) {
  return (
    <div className="flex flex-col gap-4">
      {DeveloperProfileConstants.OPTIONS.aiToolCategories.map(category => (
        <AiStackCategory
          key={category.key}
          category={category}
          selected={value[category.key] ?? []}
          onSelectionChange={selections => onChange({ ...value, [category.key]: selections })}
        />
      ))}
    </div>
  );
}
